import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { toast } from "react-hot-toast";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { Helmet } from 'react-helmet-async';
import axiosInstance from "../utils/axiosInstance";

const languages = ["English", "German", "Dutch", "Korean", "Spanish", "French", "Italian", "Japanese", "Korean", "Arabic", "Chinese", "Russian"];
const levels = ["A1", "A2", "B1", "B2", "C1", "C2"];

const ProfileInfo = () => {
    const { userId, token } = useContext(AuthContext);
    const [user, setUser] = useState({});
    const [editingField, setEditingField] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [showPasswordInput, setShowPasswordInput] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/users/${userId}`, {
                    headers: { Authorization: token },
                });
                setUser(res.data);
            } catch (err) {
                toast.error("Failed to fetch user information");
            }
        };
        fetchUser();
    }, [userId, token]);

    const handleUpdate = async () => {
        try {
            const updateData = { ...user };
            if (newPassword) {
                updateData.password = newPassword; // Plain password sent to the backend
                await axiosInstance.put(
                    `${process.env.REACT_APP_API_BASE}/api/auth/update-password`,
                    { userId, newPassword },
                    { headers: { Authorization: token } }
                );
                toast.success("Password updated successfully");
            } else {
                await axiosInstance.put(
                    `${process.env.REACT_APP_API_BASE}/api/users/${userId}`,
                    updateData,
                    { headers: { Authorization: token } }
                );
                toast.success("Profile updated successfully");
            }
            setEditingField(null);
            setShowPasswordInput(false);
            setNewPassword("");
        } catch (err) {
            toast.error("Failed to update profile");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-indigo-200 px-4 py-6">
            <Helmet>
                <title>Profile Info - PairLingo</title>
                <meta name="description" content="Speak with perfect language partner on PairLingo." />
                <meta name="keywords" content="language learning, pairlingo, chat, match, speaking chat, german language partner chat, language speaking practice" />
            </Helmet>
            <div className="max-w-3xl mx-auto bg-white shadow p-6 rounded">
                <h2 className="text-2xl font-bold text-indigo-700 mb-4">Profile Information</h2>

                <div className="space-y-4">
                    {/* Username (Uneditable) */}
                    <div className="flex items-center justify-between">
                        <label className="font-semibold">Username:</label>
                        <p>{user.username}</p>
                    </div>

                    {/* Bio (Editable) */}
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="font-semibold">Bio:</label>
                            {editingField === "bio" ? (
                                <input
                                    type="text"
                                    value={user.bio || ""}
                                    onChange={(e) => setUser({ ...user, bio: e.target.value })}
                                    className="border px-2 py-1 rounded w-full"
                                />
                            ) : (
                                <p>{user.bio}</p>
                            )}
                        </div>
                        <button onClick={() => setEditingField("bio")}>
                            <PencilSquareIcon className="w-5 h-5 text-gray-600 hover:text-indigo-600" />
                        </button>
                    </div>

                    {/* Dropdown for Current Language */}
                    <div className="flex items-center justify-between">
                        <label className="font-semibold">Current Language:</label>
                        {editingField === "currentLanguage" ? (
                            <select
                                value={user.currentLanguage}
                                onChange={(e) => setUser({ ...user, currentLanguage: e.target.value })}
                                className="border px-2 py-1 rounded w-full bg-white"
                            >
                                {languages.map((lang) => (
                                    <option key={lang} value={lang}>{lang}</option>
                                ))}
                            </select>
                        ) : (
                            <p>{user.currentLanguage}</p>
                        )}
                        <button onClick={() => setEditingField("currentLanguage")}>
                            <PencilSquareIcon className="w-5 h-5 text-gray-600 hover:text-indigo-600" />
                        </button>
                    </div>

                    {/* Dropdown for Target Language */}
                    <div className="flex items-center justify-between">
                        <label className="font-semibold">Target Language:</label>
                        {editingField === "targetLanguage" ? (
                            <select
                                value={user.targetLanguage}
                                onChange={(e) => setUser({ ...user, targetLanguage: e.target.value })}
                                className="border px-2 py-1 rounded w-full bg-white"
                            >
                                {languages.map((lang) => (
                                    <option key={lang} value={lang}>{lang}</option>
                                ))}
                            </select>
                        ) : (
                            <p>{user.targetLanguage}</p>
                        )}
                        <button onClick={() => setEditingField("targetLanguage")}>
                            <PencilSquareIcon className="w-5 h-5 text-gray-600 hover:text-indigo-600" />
                        </button>
                    </div>

                    {/* Dropdown for Level */}
                    <div className="flex items-center justify-between">
                        <label className="font-semibold">Level:</label>
                        {editingField === "level" ? (
                            <select
                                value={user.level}
                                onChange={(e) => setUser({ ...user, level: e.target.value })}
                                className="border px-2 py-1 rounded w-full bg-white"
                            >
                                {levels.map((level) => (
                                    <option key={level} value={level}>{level}</option>
                                ))}
                            </select>
                        ) : (
                            <p>{user.level}</p>
                        )}
                        <button onClick={() => setEditingField("level")}>
                            <PencilSquareIcon className="w-5 h-5 text-gray-600 hover:text-indigo-600" />
                        </button>
                    </div>

                    {/* Password Change */}
                    <div className="flex items-center justify-between">
                        <label className="font-semibold">Change Password:</label>
                        {showPasswordInput ? (
                            <input
                                type="password"
                                placeholder="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="border px-2 py-1 rounded w-full"
                            />
                        ) : (
                            <button
                                onClick={() => setShowPasswordInput(true)}
                                className="text-sm text-indigo-600 hover:underline"
                            >
                                Change Password
                            </button>
                        )}
                    </div>

                    <div className="mt-4">
                        <button
                            onClick={handleUpdate}
                            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-500"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileInfo;
